import config from '../config';
import { formatIstDateTime } from './marketingApi';

const CACHE_TTL_MS = 2 * 60 * 1000;
const cache = new Map();
const IST_OFFSET = '+05:30';
const IST_TZ = 'Asia/Kolkata';

const API = config.customerApiBase;

/** users.call_logs timestamps are stored as naive IST rather than UTC. */
export function formatOpsCallDateTime(value) {
  if (value == null || value === '') return '—';
  const raw = String(value).trim();
  if (!raw) return '—';
  let normalized = raw;
  if (/[zZ]$/.test(raw)) normalized = `${raw.slice(0, -1)}${IST_OFFSET}`;
  else if (!/[+-]\d{2}:\d{2}$/.test(raw)) normalized = `${raw}${IST_OFFSET}`;
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-IN', {
    timeZone: IST_TZ,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

export function formatCallStatus(status) {
  const s = String(status || '').trim().toLowerCase();
  if (!s) return '—';
  const labels = {
    completed: 'Completed',
    connected: 'Connected',
    done: 'Done',
    answered: 'Answered',
    failed: 'Failed',
    initiated: 'Initiated',
    dialing: 'Dialing',
    ringing: 'Ringing',
    queued: 'Queued',
    active: 'Active',
    busy: 'Busy',
    no_answer: 'No answer',
    answered_silent: 'Answered silent',
    cancelled: 'Cancelled',
    disconnected: 'Disconnected',
  };
  return labels[s] || s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Badge tone for status pill styling. */
export function getCallStatusTone(status) {
  const s = String(status || '').trim().toLowerCase();
  if (s === 'completed' || s === 'connected' || s === 'done' || s === 'answered') return 'success';
  if (s === 'failed' || s === 'disconnected' || s === 'cancelled') return 'danger';
  if (s === 'no_answer' || s === 'busy' || s === 'answered_silent') return 'warning';
  if (s === 'initiated' || s === 'dialing' || s === 'ringing' || s === 'queued' || s === 'active') return 'info';
  return 'neutral';
}

function normalizePhoneKey(raw) {
  const digits = String(raw || '').replace(/\D/g, '');
  if (digits.length >= 10) return digits.slice(-10);
  return digits || '';
}

function phoneQueryParam(raw) {
  const trimmed = String(raw || '').trim();
  if (trimmed.startsWith('+')) return trimmed;
  const key = normalizePhoneKey(raw);
  if (key.length === 10) return `+91${key}`;
  return key || trimmed;
}

function getCacheEntry(phoneKey) {
  const entry = cache.get(phoneKey);
  if (!entry) return null;
  if (Date.now() - entry.at > CACHE_TTL_MS) {
    cache.delete(phoneKey);
    return null;
  }
  return entry;
}

/** Same filter as CustomerDetailsModal operator tab. */
export function filterOperatorCallLogs(logs) {
  return (logs || []).filter((log) => {
    if (log.entry_kind === 'ops_event') return false;
    if (String(log.source || '').toLowerCase() === 'segment_flow') return false;
    if (log.action_type && !log.status && !log.sim_number) return false;
    return true;
  });
}

/** Customer details → Call History → Operator Calls (preferred when customer_id known). */
export async function fetchCustomerOperatorCallLogs(
  customerId,
  { limit = 5, offset = 0 } = {},
) {
  if (!customerId) return { logs: [], total: 0 };
  const res = await fetch(
    `${API}/customers/${customerId}/call-logs?limit=${limit}&offset=${offset}`,
  );
  if (!res.ok) throw new Error(`Call logs HTTP ${res.status}`);
  const data = await res.json();
  const logs = filterOperatorCallLogs(data.data || []);
  const total = data.total != null ? Number(data.total) : logs.length;
  return { logs, total };
}

/** Paginated call history — lazy fetch on dropdown open; 2 min cache per phone + page. */
export async function fetchCallLogsByPhone(phone, { page = 1, pageSize = 10, leadId = null, customerId = null } = {}) {
  const phoneKey = normalizePhoneKey(phone);
  if (!phoneKey) return { calls: [], total: 0, limit: pageSize, offset: 0 };

  // Cache key includes lead/customer for correct isolation
  const cacheKey = `${phoneKey}|${leadId || ''}|${customerId || ''}`;

  let entry = getCacheEntry(cacheKey);
  if (!entry) {
    entry = { at: Date.now(), pages: new Map() };
    cache.set(cacheKey, entry);
  }

  if (entry.pages.has(page)) {
    return entry.pages.get(page);
  }

  const offset = (page - 1) * pageSize;
  const params = new URLSearchParams({
    phone: phoneQueryParam(phone),
    limit: String(pageSize),
    offset: String(offset),
  });
  if (leadId) params.set('lead_id', String(leadId));
  if (customerId) params.set('customer_id', String(customerId));
  const res = await fetch(`${API}/call-logs/by-phone?${params}`);
  if (!res.ok) throw new Error(`Call logs HTTP ${res.status}`);
  const data = await res.json();
  entry.pages.set(page, data);
  entry.at = Date.now();
  return data;
}

export { formatIstDateTime };
